import { handleActions, createAction } from "redux-actions";
import { api } from "../../services";
import { rootReducerInterface } from "../../typings/interfaces";
import {
  configurationInterface,
  LocationI,
  MakeI,
  ResolvedLocationI,
  SubcategoryI
} from "../../typings/configurationTypes";
import { NoneHrCountryId } from "../../models/enums";
import { mapCategoryToEn, mapModuleToEn } from "../LanguageMapper";

// Scoped configuration datasets (makes, subcategories, locations).
// Every dataset is fetched on demand, keyed by its scope, and kept for the
// lifetime of the app. The api layer deduplicates concurrent requests for the
// same URL, but every dispatched fetch still lands as its own action, so the
// per-id location fetches below are guarded against duplicates (see fetchOnce).

const CONFIGURATION_MAKES_GET = "CONFIGURATION_MAKES_GET";
const CONFIGURATION_SUBCATEGORIES_GET = "CONFIGURATION_SUBCATEGORIES_GET";
const CONFIGURATION_COUNTRIES_GET = "CONFIGURATION_COUNTRIES_GET";
const CONFIGURATION_POPULAR_LOCATIONS_GET = "CONFIGURATION_POPULAR_LOCATIONS_GET";
const CONFIGURATION_LOCATION_CHILDREN_GET = "CONFIGURATION_LOCATION_CHILDREN_GET";
const CONFIGURATION_LOCATION_GET = "CONFIGURATION_LOCATION_GET";
const CONFIGURATION_RESOLVED_LOCATION_GET = "CONFIGURATION_RESOLVED_LOCATION_GET";
const CONFIGURATION_RESET = "CONFIGURATION_RESET";

export const subcategoriesKey = (module: string, category: string) =>
  `${mapModuleToEn(module)}/${mapCategoryToEn(category, module)}`;

// Silently swallow 404 / network errors for lookups: a missing location is a
// valid answer (e.g. the "Izvan Hrvatske" filter id), not something to toast.
const ignoreError = () => null;

// Actions
export const fetchMakes = createAction(
  CONFIGURATION_MAKES_GET,
  async (category: string) => {
    const categoryEn = mapCategoryToEn(category);
    try {
      const makes: MakeI[] = await api.Configuration.makes(categoryEn);
      return { category: categoryEn, makes: makes || [] };
    } catch (error) {
      return { category: categoryEn, makes: [] };
    }
  }
);

export const fetchSubcategories = createAction(
  CONFIGURATION_SUBCATEGORIES_GET,
  async (module: string, category: string) => {
    const key = subcategoriesKey(module, category);
    try {
      const subcategories: SubcategoryI[] = await api.Configuration.subcategories(
        mapModuleToEn(module),
        mapCategoryToEn(category, module)
      );
      return { key, subcategories: subcategories || [] };
    } catch (error) {
      return { key, subcategories: [] };
    }
  }
);

export const fetchCountries = createAction(
  CONFIGURATION_COUNTRIES_GET,
  async () => {
    try {
      const countries: LocationI[] = await api.Configuration.countries();
      return countries || [];
    } catch (error) {
      return [];
    }
  }
);

export const fetchPopularLocations = createAction(
  CONFIGURATION_POPULAR_LOCATIONS_GET,
  async () => {
    try {
      const locations: LocationI[] = await api.Configuration.popularLocations();
      return locations || [];
    } catch (error) {
      return [];
    }
  }
);

export const fetchLocationChildren = createAction(
  CONFIGURATION_LOCATION_CHILDREN_GET,
  async (parentId: string) => {
    // "Izvan Hrvatske" is a filter value, not a tree node (404 on the API).
    if (!parentId || parentId === NoneHrCountryId) {
      return { parentId, children: [] };
    }
    try {
      const children: LocationI[] = await api.Configuration.locationChildren(
        parentId,
        ignoreError
      );
      return { parentId, children: children || [] };
    } catch (error) {
      return { parentId, children: [] };
    }
  }
);

// Per-id location fetches are dispatched by consumers (useLocations, the search
// tag builder) for every id still missing from the store, and they do so again
// on every store change. Without this guard each response landing re-queued the
// remaining ids, and the resulting action storm froze the search page once about five
// or more locations were selected. A duplicate dispatch gets a null payload,
// which the reducer ignores.
const locationsInFlight: Set<string> = new Set();
const resolvedLocationsInFlight: Set<string> = new Set();

const fetchOnce = (
  inFlight: Set<string>,
  id: string,
  request: () => Promise<any>
): Promise<{ id: string; location: any }> | null => {
  if (inFlight.has(id)) {
    return null;
  }

  inFlight.add(id);

  return request().then(
    location => {
      inFlight.delete(id);
      return { id, location: location || null };
    },
    () => {
      inFlight.delete(id);
      return { id, location: null };
    }
  );
};

export const fetchLocation = createAction(CONFIGURATION_LOCATION_GET, (id: string) =>
  fetchOnce(locationsInFlight, id, () => api.Configuration.location(id, ignoreError))
);

export const fetchResolvedLocation = createAction(
  CONFIGURATION_RESOLVED_LOCATION_GET,
  (id: string) =>
    fetchOnce(resolvedLocationsInFlight, id, () =>
      api.Configuration.resolveLocation({ id }, ignoreError)
    )
);

// Drops every cached dataset so the next consumer fetches it again, e.g.
// after an admin added or renamed a make, model or location.
export const resetConfiguration = createAction(CONFIGURATION_RESET);

// Selectors
const selectConfiguration = (state: rootReducerInterface): configurationInterface =>
  state.configuration;

export const selectMakes = (state: rootReducerInterface, category: string): MakeI[] | undefined =>
  category ? selectConfiguration(state).makes[mapCategoryToEn(category)] : undefined;

export const selectSubcategories = (
  state: rootReducerInterface,
  module: string,
  category: string
): SubcategoryI[] | undefined =>
  module && category
    ? selectConfiguration(state).subcategories[subcategoriesKey(module, category)]
    : undefined;

export const selectCountries = (state: rootReducerInterface) =>
  selectConfiguration(state).countries;

export const selectPopularLocations = (state: rootReducerInterface) =>
  selectConfiguration(state).popularLocations;

export const selectLocationChildren = (
  state: rootReducerInterface,
  parentId: string
): LocationI[] | undefined =>
  parentId ? selectConfiguration(state).locationChildren[parentId] : undefined;

export const selectLocation = (
  state: rootReducerInterface,
  id: string
): LocationI | null | undefined =>
  id ? selectConfiguration(state).locations[id] : undefined;

export const selectLocations = (state: rootReducerInterface) =>
  selectConfiguration(state).locations;

export const selectResolvedLocation = (
  state: rootReducerInterface,
  id: string
): ResolvedLocationI | null | undefined =>
  id ? selectConfiguration(state).resolvedLocations[id] : undefined;

export const selectResolvedLocations = (state: rootReducerInterface) =>
  selectConfiguration(state).resolvedLocations;

// Reducer
const initialState: configurationInterface = {
  makes: {},
  subcategories: {},
  countries: null,
  popularLocations: null,
  locationChildren: {},
  locations: {},
  resolvedLocations: {}
};

export default handleActions(
  {
    [CONFIGURATION_MAKES_GET]: (state: configurationInterface, { payload }) => ({
      ...state,
      makes: { ...state.makes, [payload.category]: payload.makes }
    }),
    [CONFIGURATION_SUBCATEGORIES_GET]: (state: configurationInterface, { payload }) => ({
      ...state,
      subcategories: { ...state.subcategories, [payload.key]: payload.subcategories }
    }),
    [CONFIGURATION_COUNTRIES_GET]: (state: configurationInterface, { payload }) => ({
      ...state,
      countries: payload
    }),
    [CONFIGURATION_POPULAR_LOCATIONS_GET]: (state: configurationInterface, { payload }) => ({
      ...state,
      popularLocations: payload
    }),
    [CONFIGURATION_LOCATION_CHILDREN_GET]: (state: configurationInterface, { payload }) => ({
      ...state,
      locationChildren: {
        ...state.locationChildren,
        [payload.parentId]: payload.children
      }
    }),
    // A null payload is a de-duplicated dispatch; an id already in the store is
    // kept as is, so a late duplicate never produces a new state object.
    [CONFIGURATION_LOCATION_GET]: (state: configurationInterface, { payload }) =>
      !payload || state.locations[payload.id] !== undefined
        ? state
        : {
          ...state,
          locations: { ...state.locations, [payload.id]: payload.location }
        },
    [CONFIGURATION_RESOLVED_LOCATION_GET]: (state: configurationInterface, { payload }) =>
      !payload || state.resolvedLocations[payload.id] !== undefined
        ? state
        : {
          ...state,
          resolvedLocations: { ...state.resolvedLocations, [payload.id]: payload.location }
        },
    [CONFIGURATION_RESET]: () => initialState
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Configuration/configuration.ducks.ts